// src/pages/QuoteDetail.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import EnhancedQuoteCard from '../components/EnhancedQuoteCard';
import DemocracyInsights from '../components/DemocracyInsights';
import { Helmet } from 'react-helmet-async';
import { MobileDetailView, LoadingIndicator } from '../components/mobile';

// Helper function to extract YouTube thumbnail
const getYouTubeThumbnail = (url) => {
  if (!url) return null;
  
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  
  if (match && match[2].length === 11) {
    return `https://img.youtube.com/vi/${match[2]}/hqdefault.jpg`;
  }
  
  return '/images/social-share.jpg';
};

// Get veracity color in slate theme
const getVeracityColor = (rating) => {
  switch(rating?.toLowerCase()) {
    case 'false': return 'bg-red-100 text-red-800';
    case 'misleading': return 'bg-orange-100 text-orange-800';
    case 'partly true': return 'bg-yellow-100 text-yellow-800';
    case 'mostly true': return 'bg-slate-200 text-slate-800';
    case 'true': return 'bg-green-100 text-green-800';
    default: return 'bg-slate-100 text-slate-800';
  }
};

// Format date to MM/DD/YYYY
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};

const QuoteDetail = () => {
  // Get id and slug from URL params - the Router config supports both formats:
  // /q/123.html (old style) and /q/123/quote-slug.html (new style)
  const { id, slug } = useParams();
  // For old format URLs, the id might have .html which we need to clean
  const cleanId = id?.replace('.html', '');
  const navigate = useNavigate();
  const location = useLocation();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const slugRef = useRef(slug); // Keep track of slug for URL corrections

  // Generate a slug from quote text
  const generateSlug = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 60)
      .replace(/-+$/, ''); // Remove trailing dashes
  };

  useEffect(() => {
    // Function to fetch a single quote
    const fetchQuote = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Add cache-busting parameter
        const cacheBuster = new Date().getTime();
        const response = await fetch(`/api/quotes/${cleanId}?_=${cacheBuster}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Quote not found');
          }
          if (response.status === 500) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(`Server error: ${errorData.details || errorData.error || 'Unknown server error'}`);
          }
          const text = await response.text();
          throw new Error(`API error (${response.status}): ${text}`);
        }
        
        const data = await response.json();
        setQuote(data);
        
        // Generate slug for SEO friendly URL - we do this after fetching the quote
        const quoteText = data.claim_text || data.text || '';
        const generatedSlug = generateSlug(quoteText);
        
        // Check if we need to redirect to the proper slugged URL
        // Conditions for redirect:
        // 1. We have a valid generated slug
        // 2. Either: No slug in URL (old format) OR slug in URL doesn't match generated slug
        if (generatedSlug && 
            (!slugRef.current || generatedSlug !== slugRef.current)) {
          
          // Update URL to include the proper slug (without triggering a reload)
          const newUrl = `/q/${cleanId}/${generatedSlug}.html${location.search}`;
          window.history.replaceState(null, '', newUrl);
          
          // Store the new slug
          slugRef.current = generatedSlug;
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching quote:', error);
        setError(error.message);
        setLoading(false);
      }
    };

    fetchQuote();
  }, [cleanId, retryCount, location.search]);

  // Handle retry
  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };

  // Handle tag click - navigate to home with the selected tag
  const handleTagClick = (tag) => {
    navigate('/', { state: { selectedTag: tag } });
  };

  // State for dynamic meta tags
  const [metaTags, setMetaTags] = useState({
    title: 'Fact Check',
    description: 'Democracy Defender Project',
    image: '/images/social-share.jpg',
    url: window.location.href
  });
  
  // Fetch dynamic meta tags
  useEffect(() => {
    if (quote && quote.id) {
      const fetchMetaTags = async () => {
        try {
          console.log(`Fetching meta tags for quote ${cleanId}`);
          const response = await fetch(`/api/quotes/${cleanId}/meta`);
          
          if (response.ok) {
            const metaData = await response.json();
            console.log('Successfully fetched meta tags:', metaData);
            setMetaTags(metaData);
          } else {
            console.error('Meta tag API error:', response.status);
            // Get error message if available
            try {
              const errorData = await response.json();
              console.error('Error details:', errorData);
            } catch (e) {
              console.error('Could not parse error response');
            }
            
            // Fallback to client-side generated tags
            generateFallbackMetaTags();
          }
        } catch (error) {
          console.error('Error fetching meta tags:', error);
          // Fallback to client-side generated tags if API fails
          generateFallbackMetaTags();
        }
      };
      
      // Generate fallback meta tags from the quote data
      const generateFallbackMetaTags = () => {
        const claimText = quote.claim_text || quote.text || '';
        const realityText = quote.reality_text || quote.text || '';
        
        setMetaTags({
          title: `Fact Check: "${claimText.substring(0, 60)}${claimText.length > 60 ? '...' : ''}"`,
          description: realityText.substring(0, 160) + (realityText.length > 160 ? '...' : ''),
          image: '/images/social-share.jpg',
          url: window.location.href
        });
      };
      
      fetchMetaTags();
    }
  }, [quote, cleanId]);

  if (loading) {
    return (
      <div className="w-full">
        <LoadingIndicator size="large" label="Loading fact check..." />
      </div>
    );
  }

  if (error) {
    return (
      <MobileDetailView
        backUrl="/q/"
        backLabel="All claims"
        contentClassName="w-full"
      >
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl mb-4">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
        
        <button 
          onClick={handleRetry}
          className="bg-slate-500 hover:bg-slate-600 text-white font-medium py-2 px-4 rounded-md mobile-touch-target"
        >
          Retry
        </button>
      </MobileDetailView>
    );
  }

  if (!quote) {
    return (
      <MobileDetailView
        backUrl="/q/"
        backLabel="All claims"
        contentClassName="w-full"
      >
        <div className="bg-slate-100 border border-slate-300 text-slate-700 px-4 py-3 rounded-xl">
          <p className="font-bold">Claim Not Found</p>
          <p>The requested claim could not be found.</p>
        </div>
      </MobileDetailView>
    );
  }

  // Helper to extract just the person name from source format like "Person Name, Context"
  const extractPersonName = (sourceText) => {
    if (!sourceText) return "Unknown";
    // If the source contains a comma, take only the part before the first comma
    const commaIndex = sourceText.indexOf(',');
    if (commaIndex > 0) {
      return sourceText.substring(0, commaIndex).trim();
    }
    return sourceText;
  };

  // Get structured data from the metaTags if available, or generate it
  const getStructuredData = () => {
    // If we have structured data from the API, use it
    if (metaTags && metaTags.structuredData) {
      // Update the URL to ensure it uses our canonical URL
      return {
        ...metaTags.structuredData,
        url: canonicalUrl
      };
    }
    
    // Otherwise generate it from the quote data
    if (!quote) return null;
    
    // Generate rating based on veracity_rating
    let ratingValue = 0;
    let ratingName = "False";
    
    if (quote.veracity_rating) {
      switch(quote.veracity_rating.toLowerCase()) {
        case 'false':
          ratingValue = 0;
          ratingName = "False";
          break;
        case 'misleading':
          ratingValue = 0.25;
          ratingName = "Misleading";
          break;
        case 'partly true':
          ratingValue = 0.5;
          ratingName = "Partly True";
          break;
        case 'mostly true':
          ratingValue = 0.75;
          ratingName = "Mostly True";
          break;
        case 'true':
          ratingValue = 1;
          ratingName = "True";
          break;
        default:
          ratingValue = 0;
          ratingName = "False";
      }
    }
    
    // Format date for schema
    const formatSchemaDate = (dateString) => {
      if (!dateString) return new Date().toISOString().split('T')[0];
      const date = new Date(dateString);
      return date.toISOString().split('T')[0]; // YYYY-MM-DD format
    };
    
    // Get clean text for the quote (for use in structured data)
    const cleanQuoteText = (quote.claim_text || quote.text || "").replace(/"/g, '\"');
    const cleanRealityText = (quote.reality_text || quote.text || "").replace(/"/g, '\"');
    
    const claimReviewData = {
      "@context": "https://schema.org",
      "@type": "ClaimReview",
      "datePublished": formatSchemaDate(quote.claim_date || quote.date),
      "dateModified": formatSchemaDate(quote.updated_at || quote.claim_date || quote.date),
      "url": canonicalUrl,
      "claimReviewed": cleanQuoteText,
      "itemReviewed": {
        "@type": "Claim",
        "appearance": {
          "@type": "CreativeWork",
          "name": quote.claim_source || quote.source || "",
          "url": quote.citation || canonicalUrl
        },
        "datePublished": formatSchemaDate(quote.claim_date || quote.date),
        "author": {
          "@type": "Person",
          "name": extractPersonName(quote.claim_source || quote.source || "Unknown")
        }
      },
      "author": {
        "@type": "Organization",
        "name": "Democracy Analysis Project",
        "url": window.location.origin,
        "logo": {
          "@type": "ImageObject",
          "url": `${window.location.origin}/icons/favicon-512x512.png`,
          "width": "512",
          "height": "512"
        }
      },
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": ratingValue,
        "bestRating": 1,
        "worstRating": 0,
        "alternateName": ratingName
      },
      "reviewBody": cleanRealityText
    };
    
    return claimReviewData;
  };
  
  // Generate canonical URL with slug
  const canonicalUrl = quote && quote.id ? 
    `${window.location.origin}/q/${cleanId}/${
      slugRef.current || generateSlug(quote.claim_text || quote.text || '')
    }.html` : 
    window.location.href;

  // Get next and previous quote IDs if available
  // (Added as placeholder - we need to fetch these from the API in a real implementation)
  const [prevQuoteId, nextQuoteId] = [null, null];
  
  // Get quote title for header
  const quoteTitle = quote?.claim_text || quote?.text || "Fact Check";
  
  return (
    <>
      <Helmet>
        <title>{metaTags.title}</title>
        <meta name="description" content={metaTags.description} />
        
        {/* Canonical URL - critically important for SEO with our new URL structure */}
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={metaTags.title} />
        <meta property="og:description" content={metaTags.description} />
        <meta property="og:image" content={metaTags.image} />
        <meta property="og:site_name" content="Democracy Analysis Project" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={metaTags.title} />
        <meta name="twitter:description" content={metaTags.description} />
        <meta name="twitter:image" content={metaTags.image} />
        
        {/* Article-specific meta tags */}
        {quote && quote.claim_date && (
          <meta property="article:published_time" content={new Date(quote.claim_date).toISOString()} />
        )}
        
        {/* Mobile optimization */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0, minimum-scale=1.0, viewport-fit=cover" />
        
        {/* Pagination/Navigation links for related quotes */}
        {prevQuoteId && (
          <link rel="prev" href={`${window.location.origin}/q/${prevQuoteId}`} />
        )}
        {nextQuoteId && (
          <link rel="next" href={`${window.location.origin}/q/${nextQuoteId}`} />
        )}
        
        {/* Add JSON-LD structured data for Google Rich Results */}
        {quote && (
          <script type="application/ld+json">
            {JSON.stringify(getStructuredData())}
          </script>
        )}
      </Helmet>
      
      <MobileDetailView
        backUrl="/q/"
        backLabel="All claims"
        contentClassName="w-full"
      >
        <EnhancedQuoteCard
          quote={quote}
          defaultExpanded={true}
          onTagClick={handleTagClick}
        />
        
        {/* Democracy Insights component is conditionally rendered only for specific quotes */}
        {/* Temporarily disabled to fix loading indicator issue */}
      </MobileDetailView>
    </>
  );
};

export default QuoteDetail;